import { Button, Dropdown } from 'antd'
import { Check, Clock, EyeOff, MoreHorizontal } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import type { ChecklistClickTarget, ChecklistItem } from 'src/analytics'
import { ChecklistClickTarget as ClickTarget } from 'src/analytics'

import { ChecklistItemCard } from './ChecklistItemCard'
import { HOVER_ACTIVATE_DELAY_MS, SNOOZE_DAYS } from './constants'
import { useStyles } from './styles'
import type { ChecklistItemModel } from './types'

type Props = {
  items: ChecklistItemModel[]
  completedCount: number
  /** The task whose card is shown. Follows hover and keyboard focus. */
  activeKey: ChecklistItem
  onActivate: (key: ChecklistItem) => void
  onOpen: (item: ChecklistItemModel, target: ChecklistClickTarget) => void
  onHideForNow: () => void
  onDismissRequest: () => void
  /**
   * List only, no card beside it — how the panel rests until somebody asks for
   * the detail. What it says while resting is "here is what is left", which is a
   * different question from the one the card answers.
   */
  compact?: boolean
  /** Called on the first hover or focus, which is what asks for the card. */
  onLeaveCompact?: () => void
}

export const ChecklistPanel = ({
  items,
  completedCount,
  activeKey,
  onActivate,
  onOpen,
  onHideForNow,
  onDismissRequest,
  compact = false,
  onLeaveCompact
}: Props) => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()

  // The active key always comes from `items`, but a stale one must not blank the
  // pane — fall back to the first task instead of rendering nothing.
  const active = items.find((item) => item.def.key === activeKey) ?? items[0]

  /**
   * Pending hover preview. Crossing the list on the way to the card below used
   * to swap the pane once per row passed under the cursor; the wait means the
   * pointer has to settle on a task before its card is fetched into view.
   */
  const hoverTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  /**
   * A hover or a Tab is the point at which somebody wants the detail, so the
   * list gives way to the full panel and the row they landed on becomes the one
   * the card describes.
   */
  const reveal = (key: ChecklistItem) => {
    onLeaveCompact?.()
    onActivate(key)
  }

  const cancelHoverPreview = () => clearTimeout(hoverTimer.current)

  const previewAfterHover = (key: ChecklistItem) => {
    cancelHoverPreview()
    hoverTimer.current = setTimeout(() => reveal(key), HOVER_ACTIVATE_DELAY_MS)
  }

  useEffect(() => cancelHoverPreview, [])

  return (
    <div className={styles.panel}>
      <div className={styles.list}>
        <div className={styles.listHeader}>
          <div>
            <div className={styles.listTitle}>
              {t('shell.gettingStarted.title')}
            </div>
            <div className={styles.listProgress}>
              {t('shell.gettingStarted.progress', {
                done: completedCount,
                total: items.length
              })}
              {' · '}
              {t('shell.gettingStarted.encouragement')}
            </div>
          </div>
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{
              items: [
                {
                  key: 'snooze',
                  icon: <Clock size={14} />,
                  label: t('shell.gettingStarted.hideForDays', {
                    days: SNOOZE_DAYS
                  })
                },
                {
                  key: 'dismiss',
                  icon: <EyeOff size={14} />,
                  label: t('shell.gettingStarted.dismiss')
                }
              ],
              onClick: ({ key }) =>
                key === 'snooze' ? onHideForNow() : onDismissRequest()
            }}
          >
            <Button
              type="text"
              size="small"
              icon={<MoreHorizontal size={16} />}
              aria-label={t('shell.gettingStarted.more')}
            />
          </Dropdown>
        </div>

        {items.map((item) => {
          const Icon = item.def.icon
          const label = t(`shell.gettingStarted.items.${item.def.key}.title`)

          return (
            <button
              key={item.def.key}
              type="button"
              className={cx(
                styles.row,
                // Nothing is highlighted while compact: the highlight means
                // "this is the task the card is describing", and the card is
                // not there yet. A row that came up shaded would read as
                // pre-selected without anybody having chosen it.
                !compact &&
                  item.def.key === active?.def.key &&
                  styles.rowActive,
                item.done && styles.rowDone
              )}
              // Hover and focus both preview: a keyboard user tabbing through the
              // list must see the same card a mouse user sees. Only the pointer
              // waits — a Tab press is already a deliberate stop.
              onMouseEnter={() => previewAfterHover(item.def.key)}
              onMouseLeave={cancelHoverPreview}
              onFocus={() => {
                cancelHoverPreview()
                reveal(item.def.key)
              }}
              onClick={() => onOpen(item, ClickTarget.PRIMARY)}
            >
              <span className={styles.rowIcon}>
                <Icon size={15} />
              </span>
              <span
                className={cx(
                  styles.rowLabel,
                  item.done && styles.rowLabelDone
                )}
              >
                {label}
              </span>
              {item.done && (
                <span className={styles.rowCheck}>
                  <Check size={14} />
                </span>
              )}
            </button>
          )
        })}
      </div>

      {active && !compact && (
        <ChecklistItemCard item={active} onOpen={onOpen} />
      )}
    </div>
  )
}
